/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import { mergeUpdatedFlight } from "./flightEditState";

import { Button, Grid2, IconButton, Tooltip, Typography } from "@mui/material";
import {
	CallbackProperty,
	Cartesian3,
	Cartographic,
	Clock,
	ClockRange,
	Color,
	createWorldTerrainAsync,
	HeadingPitchRange,
	HeadingPitchRoll,
	Ion,
	IonResource,
	JulianDate,
	ModelGraphics,
	PolygonGraphics,
	PolylineGraphics,
	SampledPositionProperty,
	sampleTerrainMostDetailed,
	SceneMode,
	Terrain,
	TrackingReferenceFrame,
	Transforms,
	Viewer,
} from "cesium";
import classNames from "classnames";
import {
	FaArrowDown,
	FaPause,
	FaPlane,
	FaPlay,
	FaRegMap,
	FaStop,
} from "react-icons/fa";
import { useParams } from "react-router-dom";

import {
	EHeightMeasurementUnit,
	ESpeedMeasurementUnit,
	TFlightLog,
	TFlightLogComment,
	THeartRate,
} from "../../../../@types";
import { apis, callApi } from "../../../../api";
import { useAccount } from "../../../../hooks/useAccount";
import {
	calculateWind,
	convertHeight,
	convertSpeed,
	distance3D,
	LandingDetector,
	normalizeGpsData,
} from "../../../../utils";
import { Container } from "../../../common/Container/Container";
import { FlightDetail } from "./components/FlightDetail";
import { ListFlightLogs } from "./components/ListFlightLogs";
import { TLocation } from "./FlightLogs";
import { GoogleMapsService } from "../../../../services/GoogleMapsService";
import moment from "moment";

export function FlightLogsCesium() {
	const params = useParams();
	const flightId = params.id;
	const viewerRef = useRef<Viewer | null>(null);
	const countRef = useRef(0);
	const count2Ref = useRef(0);
	const timeSliderRef = useRef<HTMLInputElement>(null);
	const polygonRef = useRef<string[]>([]);
	const prevIndex = useRef(-2);
	const [flightLog, setFlightLog] = useState<TFlightLog | null>(null);
	const statusRef = useRef<"play" | "pause" | "stop">("pause");
	const { account } = useAccount();

	type TState = {
		loading: boolean;
		gpsData: TLocation[];
		flightLogs: TFlightLog[];
		comments: TFlightLogComment[];
		heartRateData: THeartRate[];
		landing: number | null;
		distance: number | null;
		speed: number;
		heading: number;
		height: number;
		wind: { windSpeed: number; windDirection: number } | null;
		status: "play" | "pause" | "stop";
		interval: number;
		elevation: number;
		cesiumHeight: number;
		currentTime: number;
		heartRate: number | null;
		mapError: boolean;
	};
	const [state, setState] = useState<TState>({
		loading: false,
		gpsData: [],
		flightLogs: [],
		comments: [],
		heartRateData: [],
		landing: 0,
		distance: 0,
		speed: 0,
		heading: 0,
		height: 0,
		wind: null,
		status: "stop",
		interval: 1000,
		elevation: 0,
		cesiumHeight: 0,
		currentTime: 0,
		heartRate: null,
		mapError: false,
	});
	const [selectedMode, setSelectedMode] = useState<"default" | "tail">(
		"default",
	);

	useEffect(() => {
		const getFlightLogs = async () => {
			const response = await callApi({
				method: "GET",
				url: apis.MY_FLIGHT_LOGS,
			});

			if (!response) {
				return;
			}

			setState((prevState) => ({
				...prevState,
				flightLogs: response.flights,
			}));
		};

		getFlightLogs();
	}, []);

	useEffect(() => {
		let gpsData: TLocation[] = [];
		let elevation = 0;
		let cesiumHeight = 0;
		let heartRates: THeartRate[] | null = [];

		const scenePostRenderListener = () => {
			if (count2Ref.current % 10 === 0) {
				setState((prevState) => {
					if (prevState.loading) {
						const mainPath = viewerRef.current?.entities.getById("main-path");
						if (mainPath) {
							return { ...prevState, loading: false };
						}
					}

					return prevState;
				});
			}
			count2Ref.current++;
		};

		const clockOnTickListener = (clock: Clock) => {
			if (countRef.current % 10 === 0) {
				const length = gpsData.length;
				const index = gpsData.findIndex(
					(point) =>
						point.time > JulianDate.toDate(clock.currentTime).getTime(),
				);
				if (timeSliderRef.current) {
					timeSliderRef.current.value = `${JulianDate.toDate(clock.currentTime).getTime() - gpsData[0].time}`;
				}

				if (index === -1) {
					setState((prevState) => ({
						...prevState,
						speed: gpsData[length - 1]?.speed ?? 0,
						heading: gpsData[length - 1]?.bearing ?? 0,
						height:
							gpsData[length - 1]?.mslAltitude ??
							gpsData[length - 1]?.altitude ??
							0,
						wind: gpsData[length - 1]?.calculatedData?.wind ?? null,
						currentTime: JulianDate.toDate(clock.currentTime).getTime(),
						status: "pause",
					}));
					statusRef.current = "pause";
				} else if (index < 2) {
					setState((prevState) => ({
						...prevState,
						speed: gpsData[0]?.speed ?? 0,
						heading: gpsData[0]?.bearing ?? 0,
						height: gpsData[0]?.mslAltitude ?? gpsData[0]?.altitude ?? 0,
						wind: gpsData[0]?.calculatedData?.wind ?? null,
						currentTime: JulianDate.toDate(clock.currentTime).getTime(),
					}));
				} else {
					setState((prevState) => ({
						...prevState,
						speed: gpsData[index - 1].speed ?? 0,
						heading: gpsData[index - 1]?.bearing ?? 0,
						height:
							gpsData[index - 1].mslAltitude ??
							gpsData[index - 1].altitude ??
							0,
						wind: gpsData[index - 1].calculatedData?.wind ?? null,
						currentTime: JulianDate.toDate(clock.currentTime).getTime(),
					}));
				}

				if (heartRates && heartRates.length > 0) {
					const tempHeartRate = heartRates;
					const heartRateIndex = heartRates.findIndex(
						(heartRate) =>
							heartRate.time > JulianDate.toDate(clock.currentTime).getTime(),
					);
					if (heartRateIndex === -1) {
						setState((prevState) => ({
							...prevState,
							heartRate: tempHeartRate[tempHeartRate.length - 1].heartRate,
						}));
					} else if (heartRateIndex < 2) {
						setState((prevState) => ({
							...prevState,
							heartRate: tempHeartRate[0].heartRate,
						}));
					} else {
						setState((prevState) => ({
							...prevState,
							heartRate: tempHeartRate[heartRateIndex - 1].heartRate,
						}));
					}
				}

				if (index >= 0 && viewerRef.current && statusRef.current === "play") {
					const highlightPath =
						viewerRef.current.entities.getById("highlight-path");
					if (
						highlightPath?.polyline?.positions &&
						Math.abs(prevIndex.current - index) > 10
					) {
						(
							highlightPath.polyline as PolylineGraphics.ConstructorOptions
						).positions = Cartesian3.fromDegreesArrayHeights(
							gpsData
								.slice(
									Math.max(index - 60, 0),
									Math.min(index + 60, gpsData.length),
								)
								.flatMap((point) => [
									point.longitude,
									point.latitude,
									(point.mslAltitude ?? point.altitude) -
										elevation +
										cesiumHeight,
								]),
						);
						prevIndex.current = index;
					}
				}
			}
			countRef.current++;
		};

		const getFlightDetail = async () => {
			setFlightLog(null);
			setState((prevState) => ({
				...prevState,
				loading: false,
				gpsData: [],
				comments: [],
				heartRateData: [],
				landing: null,
				distance: null,
				speed: 0,
				heading: 0,
				height: 0,
				wind: null,
				elevation: 0,
				status: "stop",
				mapError: false,
			}));
			countRef.current = 0;
			statusRef.current = "stop";
			polygonRef.current = [];
			prevIndex.current = -2;
			if (!flightId) {
				return;
			}
			setState((prevState) => ({
				...prevState,
				loading: true,
			}));
			try {
				const getFlightResponse = await callApi({
					method: "GET",
					url: `${apis.FLIGHT_LOGS}/${flightId}`,
				});
				if (!getFlightResponse) {
					return;
				}
				const flightLog: TFlightLog = getFlightResponse.flight;
				setFlightLog(flightLog);
				setState((prevState) => ({
					...prevState,
					comments: getFlightResponse.comments ?? [],
				}));
				if (getFlightResponse.flight?.heartlocation) {
					try {
						const downloadHeartRateURLResponse = await callApi({
							method: "POST",
							url: apis.GET_DOWNLOAD_URL,
							body: {
								location: getFlightResponse.flight?.heartlocation,
								fileName: getFlightResponse.flight?.heartlocation
									?.split("/")
									.pop(),
							},
						});
						if (downloadHeartRateURLResponse?.downloadURL) {
							const heartRateResponse = await callApi({
								method: "GET",
								url: downloadHeartRateURLResponse.downloadURL,
								baseURL: "",
								responseType: "json",
							});
							heartRates = heartRateResponse as THeartRate[];
							setState((prevState) => ({
								...prevState,
								heartRateData: heartRates ?? [],
							}));
						}
					} catch {
						// Heart-rate telemetry is optional; GPS replay can still load.
					}
				}
				let response;
				try {
					const downloadURLResponse = await callApi({
						method: "POST",
						url: apis.GET_DOWNLOAD_URL,
						body: {
							location: flightLog.location,
							fileName: flightLog.location?.split("/").pop(),
						},
					});
					if (!downloadURLResponse?.downloadURL) {
						throw new Error("Flight download URL is unavailable");
					}
					response = await callApi({
						method: "GET",
						url: downloadURLResponse.downloadURL,
						baseURL: "",
						responseType: "json",
					});
				} catch {
					const gpsResponse = await callApi({
						method: "POST",
						url: `${apis.FLIGHT_LOGS}/file/${flightId}`,
					});
					response = gpsResponse?.flight;
				}

				if (!response || response.length === 0) {
					setState((prevState) => ({
						...prevState,
						loading: false,
						gpsData: [],
					}));
					return;
				}

				gpsData = response;
				gpsData = gpsData.filter((point: TLocation) => point.accuracy < 100);
				if (gpsData.length < 2) {
					setState((prevState) => ({
						...prevState,
						loading: false,
						gpsData: [],
					}));
					return;
				}
				const duration = gpsData[gpsData.length - 1].time - gpsData[0].time;

				if (gpsData.length > 0) {
					gpsData = normalizeGpsData(gpsData);
					let metarWind: { speed: number; direction: number } | null = null;
					if (flightLog.weatherInfo) {
						const weather: {
							wind: {
								speed: { value: number; unit: string };
								direction: { value: number; unit: string };
							};
						} = JSON.parse(flightLog.weatherInfo);
						metarWind = {
							speed: weather.wind.speed.value * 0.514444,
							direction: weather.wind.direction.value,
						};
					}
					gpsData = calculateWind(gpsData, metarWind)
						.enhancedGPSData as TLocation[];
				}

				let totalDistance = 0;
				for (let i = 1; i < gpsData.length; i++) {
					totalDistance += distance3D(gpsData[i - 1], gpsData[i]);
				}
				const detector = new LandingDetector(gpsData);
				const detectedLandings = detector.detectLandings();
				setState((prevState) => ({
					...prevState,
					gpsData,
					landing: detectedLandings.length,
					distance: totalDistance,
				}));

				Ion.defaultAccessToken = process.env.REACT_APP_CESIUM_TOKEN ?? "";
				viewerRef.current = new Viewer("cesium-container", {
					terrain: Terrain.fromWorldTerrain({
						requestVertexNormals: true,
						requestWaterMask: true,
					}),
					fullscreenButton: false,
					homeButton: false,
					geocoder: false,
					sceneMode: SceneMode.SCENE3D,
					sceneModePicker: false,
					baseLayerPicker: false,
					navigationHelpButton: false,
					navigationInstructionsInitiallyVisible: false,
					contextOptions: {
						webgl: {
							preserveDrawingBuffer: true,
						},
					},
				});
				viewerRef.current.scene.globe.undergroundColor = Color.TRANSPARENT;
				viewerRef.current.clock.onTick.addEventListener(clockOnTickListener);

				const planePositionProperty = new SampledPositionProperty();

				const startTime = JulianDate.fromDate(new Date(gpsData[0].time));
				const stopTime = JulianDate.fromDate(
					new Date(gpsData[gpsData.length - 1].time),
				);
				viewerRef.current.clock.startTime = startTime;
				viewerRef.current.clock.stopTime = stopTime;
				viewerRef.current.clock.currentTime = startTime;
				viewerRef.current.clock.clockRange = ClockRange.CLAMPED;
				viewerRef.current.timeline.zoomTo(startTime, stopTime);
				if (timeSliderRef.current) {
					timeSliderRef.current.min = "0";
					timeSliderRef.current.max = `${gpsData[gpsData.length - 1].time - gpsData[0].time}`;
					timeSliderRef.current.step = "10";
				}

				viewerRef.current.scene.postRender.addEventListener(
					scenePostRenderListener,
				);
				const terrainProvider = await createWorldTerrainAsync();

				const step = Math.ceil(gpsData.length / 512);
				const checkPoints = gpsData.filter((_, index) => index % step === 0);

				const { ElevationService } = await GoogleMapsService.loadElevation();
				const elevationResponse =
					await new ElevationService().getElevationForLocations({
						locations: checkPoints.map((point) => ({
							lat: point.latitude,
							lng: point.longitude,
						})),
					});
				elevation = elevationResponse.results[0].elevation;
				const heights = await sampleTerrainMostDetailed(
					terrainProvider,
					checkPoints.map((point) =>
						Cartographic.fromDegrees(point.longitude, point.latitude),
					),
				);
				cesiumHeight = heights[0].height;

				gpsData = gpsData.map((point, index) => {
					return {
						...point,
						cesiumHeight: heights[Math.floor(index / step)].height,
					};
				});

				setState((prevState) => ({ ...prevState, gpsData }));

				for (let i = 0; i < gpsData.length; i++) {
					const position = Cartesian3.fromDegrees(
						gpsData[i].longitude,
						gpsData[i].latitude,
						(gpsData[i].mslAltitude ?? gpsData[i].altitude) -
							elevation +
							cesiumHeight -
							15,
					);
					planePositionProperty.addSample(
						JulianDate.fromDate(new Date(gpsData[i].time)),
						position,
					);
					if (i > 0) {
						polygonRef.current.push(`polygon-${i - 1}`);
						viewerRef.current.entities.add({
							id: `polygon-${i - 1}`,
							polygon: {
								hierarchy: Cartesian3.fromDegreesArrayHeights([
									gpsData[i - 1].longitude,
									gpsData[i - 1].latitude,
									(gpsData[i - 1].mslAltitude ?? gpsData[i - 1].altitude) -
										elevation +
										cesiumHeight,
									gpsData[i - 1].longitude,
									gpsData[i - 1].latitude,
									gpsData[i - 1].cesiumHeight ?? 0,
									gpsData[i].longitude,
									gpsData[i].latitude,
									gpsData[i].cesiumHeight ?? 0,
									gpsData[i].longitude,
									gpsData[i].latitude,
									(gpsData[i].mslAltitude ?? gpsData[i].altitude) -
										elevation +
										cesiumHeight,
								]),
								material: new Color(238 / 255, 238 / 255, 238 / 255, 0.2),
								perPositionHeight: true,
								closeBottom: false,
								closeTop: false,
							},
						});
					}
				}
				viewerRef.current.entities.add({
					id: "main-path",
					name: "Flight Path",
					polyline: {
						positions: Cartesian3.fromDegreesArrayHeights(
							gpsData.flatMap((point) => [
								point.longitude,
								point.latitude,
								(point.mslAltitude ?? point.altitude) -
									elevation +
									cesiumHeight,
							]),
						),
						width: 3,
						material: new Color(246 / 255, 170 / 255, 58 / 255, 1),
						clampToGround: false,
					},
				});

				const resource = await IonResource.fromAssetId(
					Number(process.env.REACT_APP_CESIUM_PLANE_MODEL ?? "0"),
				);
				viewerRef.current.entities.add({
					id: "plane-fake",
					position: planePositionProperty,
					orientation: new CallbackProperty((time) => {
						if (!time) {
							return;
						}
						const index = gpsData.findIndex(
							(point) => point.time > JulianDate.toDate(time).getTime(),
						);
						if (index === -1) {
							return;
						}
						const point = gpsData[index];
						if (
							point.heading === undefined ||
							point.pitch === undefined ||
							point.roll === undefined
						) {
							return;
						}

						const hpr = new HeadingPitchRoll(point.heading, 0, 0);

						return Transforms.headingPitchRollQuaternion(
							Cartesian3.fromDegrees(
								point.longitude,
								point.latitude,
								(point.mslAltitude ?? point.altitude) -
									elevation +
									cesiumHeight -
									15,
							),
							hpr,
						);
					}, false),
					model: {
						uri: resource,
						color: Color.TRANSPARENT,
					},
					trackingReferenceFrame: TrackingReferenceFrame.AUTODETECT,
					viewFrom: new Cartesian3(-400, 0, 100),
				});
				viewerRef.current.entities.add({
					id: "plane",
					position: planePositionProperty,
					orientation: new CallbackProperty((time) => {
						if (!time) {
							return;
						}
						const index = gpsData.findIndex(
							(point) => point.time > JulianDate.toDate(time).getTime(),
						);
						if (index === -1) {
							return;
						}
						const point = gpsData[index];
						if (
							point.heading === undefined ||
							point.pitch === undefined ||
							point.roll === undefined
						) {
							return;
						}

						const hpr = new HeadingPitchRoll(
							point.heading,
							point.pitch,
							point.roll,
						);

						return Transforms.headingPitchRollQuaternion(
							Cartesian3.fromDegrees(
								point.longitude,
								point.latitude,
								(point.mslAltitude ?? point.altitude) -
									elevation +
									cesiumHeight -
									15,
							),
							hpr,
						);
					}, false),
					model: {
						uri: resource,
						color: Color.TRANSPARENT,
					},
					trackingReferenceFrame: TrackingReferenceFrame.AUTODETECT,
					viewFrom: new Cartesian3(-400, 0, 100),
				});
				viewerRef.current.zoomTo(viewerRef.current.entities);

				setState((prevState) => ({
					...prevState,
					gpsData,
					landing: detectedLandings.length,
					distance: totalDistance,
					duration,
					loading: false,
					elevation,
					cesiumHeight,
					mapError: false,
				}));
			} catch (error) {
				setState((prevState) => ({
					...prevState,
					loading: false,
					mapError: prevState.gpsData.length > 1,
				}));
			}
		};

		getFlightDetail();

		return () => {
			const viewer = viewerRef.current;
			if (viewer && !viewer.isDestroyed()) {
				viewer.scene.postRender.removeEventListener(scenePostRenderListener);
				viewer.clock.onTick.removeEventListener(clockOnTickListener);
				viewer.destroy();
			}
			viewerRef.current = null;
		};
	}, [flightId]);

	const onClickPlay = () => {
		if (viewerRef.current && timeSliderRef.current) {
			viewerRef.current.clock.shouldAnimate = true;
			if (state.status === "stop") {
				viewerRef.current.clock.currentTime = JulianDate.fromDate(
					new Date(state.gpsData[0].time),
				);
				timeSliderRef.current.value = "0";
				const mainPath = viewerRef.current.entities.getById("main-path");
				if (mainPath?.polyline?.material) {
					(mainPath.polyline as PolylineGraphics.ConstructorOptions).material =
						new Color(238 / 255, 238 / 255, 238 / 255, 0.2);
				}
				for (let i = 0; i < polygonRef.current.length; i++) {
					const polygon = viewerRef.current.entities.getById(
						polygonRef.current[i],
					);
					if (polygon?.polygon?.material) {
						(polygon.polygon as PolygonGraphics.ConstructorOptions).material =
							new Color(238 / 255, 238 / 255, 238 / 255, 0.1);
					}
				}
				onClickFocusPlane();
				const plane = viewerRef.current.entities.getById("plane");
				if (plane?.model) {
					(plane.model as ModelGraphics.ConstructorOptions).color = Color.WHITE;
				}
				viewerRef.current.entities.add({
					id: "highlight-path",
					polyline: {
						positions: Cartesian3.fromDegreesArrayHeights(
							state.gpsData
								.slice(0, 60)
								.flatMap((point) => [
									point.longitude,
									point.latitude,
									(point.mslAltitude ?? point.altitude) -
										state.elevation +
										state.cesiumHeight,
								]),
						),
						width: 4,
						material: new Color(246 / 255, 170 / 255, 58 / 255, 1),
						clampToGround: false,
						zIndex: 10,
					},
				});
			}
		}
		setState((prevState) => ({ ...prevState, status: "play" }));
		statusRef.current = "play";
	};

	const onClickPause = () => {
		if (viewerRef.current) {
			viewerRef.current.clock.shouldAnimate = false;
		}
		setState((prevState) => ({ ...prevState, status: "pause" }));
		statusRef.current = "pause";
	};

	const onClickStop = () => {
		if (viewerRef.current && timeSliderRef.current) {
			viewerRef.current.zoomTo(viewerRef.current.entities);
			viewerRef.current.clock.shouldAnimate = false;
			viewerRef.current.clock.currentTime = JulianDate.fromDate(
				new Date(state.gpsData[0].time),
			);
			timeSliderRef.current.value = "0";
			const mainPath = viewerRef.current.entities.getById("main-path");
			if (mainPath?.polyline?.material) {
				(mainPath.polyline as PolylineGraphics.ConstructorOptions).material =
					new Color(246 / 255, 170 / 255, 58 / 255, 1);
				for (let i = 0; i < polygonRef.current.length; i++) {
					const polygon = viewerRef.current.entities.getById(
						polygonRef.current[i],
					);
					if (polygon?.polygon?.material) {
						(polygon.polygon as PolygonGraphics.ConstructorOptions).material =
							new Color(238 / 255, 238 / 255, 238 / 255, 0.2);
					}
				}
			}
			viewerRef.current.entities.removeById("highlight-path");
			const plane = viewerRef.current.entities.getById("plane");
			if (plane?.model) {
				(plane.model as ModelGraphics.ConstructorOptions).color =
					Color.TRANSPARENT;
			}
		}
		setState((prevState) => ({ ...prevState, status: "stop" }));
		statusRef.current = "stop";
	};

	const onClickInterval = (interval: number) => () => {
		if (viewerRef.current) {
			viewerRef.current.clock.multiplier = interval / 1000;
		}
		setState((prevState) => ({ ...prevState, interval }));
	};

	const deleteFlightLogs = (id: number) => {
		if (!id) {
			return;
		}

		setState((prevState) => {
			return {
				...prevState,
				flightLogs: prevState.flightLogs.filter((log) => log.id !== id),
			};
		});
	};

	const updateFlightLogData = (id: number, flightLogData: TFlightLog) => {
		if (!id || flightLogData?.id !== id) {
			return;
		}

		setState((prevState) => {
			return {
				...prevState,
				flightLogs: prevState.flightLogs.map((log) => {
					if (log.id !== id) {
						return log;
					}

					return mergeUpdatedFlight(log, flightLogData)!;
				}),
			};
		});
		setFlightLog((current) => mergeUpdatedFlight(current, flightLogData));
	};

	const addComment = async (content: string) => {
		if (!flightLog?.id) {
			return;
		}
		const response = await callApi({
			method: "POST",
			url: `${apis.FLIGHT_LOGS}/${flightLog.id}/comments`,
			body: { content },
		});
		if (!response?.comment) {
			return;
		}
		const comment: TFlightLogComment = {
			...response.comment,
			profile: account?.profile
				? { ...account.profile, user: account }
				: undefined,
		};
		setState((prevState) => ({
			...prevState,
			comments: [comment, ...prevState.comments],
		}));
		setFlightLog((prevFlightLog) =>
			prevFlightLog
				? {
						...prevFlightLog,
						totalComments: (prevFlightLog.totalComments ?? 0) + 1,
					}
				: prevFlightLog,
		);
	};

	const deleteComment = async (commentId: number) => {
		await callApi({
			method: "DELETE",
			url: `${apis.FLIGHT_LOGS}/comments/${commentId}`,
		});
		setState((prevState) => ({
			...prevState,
			comments: prevState.comments.filter(({ id }) => id !== commentId),
		}));
		setFlightLog((prevFlightLog) =>
			prevFlightLog
				? {
						...prevFlightLog,
						totalComments: Math.max((prevFlightLog.totalComments ?? 0) - 1, 0),
					}
				: prevFlightLog,
		);
	};

	const onClickFocusPlane = () => {
		if (viewerRef.current) {
			const plane = viewerRef.current.entities.getById("plane");
			if (plane) {
				plane.trackingReferenceFrame = TrackingReferenceFrame.AUTODETECT;
			}
			const planeFake = viewerRef.current.entities.getById("plane-fake");
			if (planeFake) {
				planeFake.trackingReferenceFrame = TrackingReferenceFrame.AUTODETECT;
				viewerRef.current.trackedEntity = planeFake;
			}
		}
		setSelectedMode("default");
	};

	const onClickFocusPlaneTailView = () => {
		if (viewerRef.current) {
			const plane = viewerRef.current.entities.getById("plane");
			if (plane) {
				plane.trackingReferenceFrame = TrackingReferenceFrame.INERTIAL;
			}
			const planeFake = viewerRef.current.entities.getById("plane-fake");
			if (planeFake) {
				planeFake.trackingReferenceFrame = TrackingReferenceFrame.INERTIAL;
				viewerRef.current.trackedEntity = planeFake;
			}
		}
		setSelectedMode("tail");
	};

	const onClickFocusMap = () => {
		viewerRef.current?.zoomTo(viewerRef.current.entities);
	};

	const onClickTopDownView = () => {
		viewerRef.current?.zoomTo(
			viewerRef.current.entities,
			new HeadingPitchRange(0, -Math.PI / 2),
		);
	};

	const renderListFlightLog = () => {
		if (!flightId) {
			return (
				<ListFlightLogs
					flightLogs={state.flightLogs}
					deleteFlightLogs={deleteFlightLogs}
				/>
			);
		}

		if (!flightLog) {
			return null;
		}

		return (
			<FlightDetail
				flightLog={flightLog}
				gpsData={state.gpsData}
				heartRateData={state.heartRateData}
				comments={state.comments}
				addComment={addComment}
				deleteComment={deleteComment}
				landing={state.landing}
				distance={state.distance}
				updateFlightLogData={updateFlightLogData}
			/>
		);
	};

	const currentTime = moment(state.currentTime);
	const timeString = `${currentTime.tz("UTC").format("DD MMM YYYY HH:mm:ss")}Z`;

	return (
		<Container
			noFooter
			className="flight-logs-content-container"
			loading={state.loading}>
			<Grid2 container>
				{renderListFlightLog()}
				{flightId && (
					<Grid2
						size={{ xs: 12, sm: 12, md: 9, lg: 9, xl: 9 }}
						position="relative">
						<div id="cesium-container" className="cesium-containers">
							{!state.loading &&
								!state.mapError &&
								state.gpsData.length > 1 && (
									<>
										<div className="map-view-controller">
											{state.status !== "stop" && (
												<Tooltip title="Aircraft mode">
													<IconButton
														className={classNames(
															"icon-button map-view-controller-icon plane-icon",
															{ selected: selectedMode === "default" },
														)}
														onClick={onClickFocusPlane}>
														<FaPlane />
													</IconButton>
												</Tooltip>
											)}
											{state.status !== "stop" && (
												<Tooltip title="Tail view mode">
													<IconButton
														className={classNames(
															"icon-button map-view-controller-icon tail-view-plane-icon",
															{ selected: selectedMode === "tail" },
														)}
														onClick={onClickFocusPlaneTailView}>
														<FaPlane />
													</IconButton>
												</Tooltip>
											)}
											{state.status === "stop" && (
												<Tooltip title="Flight path mode">
													<IconButton
														className="icon-button map-view-controller-icon"
														onClick={onClickFocusMap}>
														<FaRegMap />
													</IconButton>
												</Tooltip>
											)}
											{state.status === "stop" && (
												<Tooltip title="Top down mode">
													<IconButton
														className="icon-button map-view-controller-icon"
														onClick={onClickTopDownView}>
														<FaArrowDown />
													</IconButton>
												</Tooltip>
											)}
										</div>
										{state.status !== "stop" && (
											<div className="top-left-info">
												<Typography className="small-title-text replay-info">
													{`GS ${convertSpeed(state.speed, ESpeedMeasurementUnit.METERS_PER_SECOND, ESpeedMeasurementUnit.KNOTS).toFixed(0)} knots`}
												</Typography>
												{state.wind && (
													<Typography className="small-title-text replay-info">
														{`${state.wind.windDirection.toFixed(0)} ${state.wind.windSpeed.toFixed(0)} knots`}
													</Typography>
												)}
												{state.heartRate !== null && (
													<Typography className="small-title-text replay-info">
														{`${state.heartRate} BPM`}
													</Typography>
												)}
											</div>
										)}
										{state.status !== "stop" && (
											<div className="top-center-info">
												<Typography className="small-title-text replay-info">
													{`TRK ${state.heading.toFixed(0)}`}
												</Typography>
											</div>
										)}
										{state.status !== "stop" && (
											<div className="top-right-info">
												<Typography className="small-title-text replay-info">
													{`${convertHeight(state.height, EHeightMeasurementUnit.METER, EHeightMeasurementUnit.FOOT).toFixed(0)} ft MSL`}
												</Typography>
											</div>
										)}
										{state.status !== "stop" && (
											<div className="bottom-left-info">
												<Typography className="small-title-text replay-info">
													{timeString}
												</Typography>
											</div>
										)}
									</>
								)}
							{state.gpsData.length > 1 &&
								!state.mapError &&
								state.status === "stop" && (
									<div className="cesium-custom-controller">
										<Button
											className="normal-button primary-button no-transform"
											onClick={onClickPlay}>
											Start Playback
										</Button>
									</div>
								)}
							<div
								className="cesium-custom-controller"
								style={{
									display:
										state.loading || state.status === "stop" ? "none" : "flex",
								}}>
								<div style={{ display: "flex", alignItems: "center" }}>
									{state.status !== "play" && (
										<IconButton className="icon-button" onClick={onClickPlay}>
											<FaPlay className="normal-icon white-text" />
										</IconButton>
									)}
									{state.status === "play" && (
										<IconButton className="icon-button" onClick={onClickPause}>
											<FaPause className="normal-icon white-text" />
										</IconButton>
									)}
									<IconButton className="icon-button" onClick={onClickStop}>
										<FaStop className="normal-icon white-text" />
									</IconButton>
									<IconButton
										className="icon-button"
										onClick={onClickInterval(1000)}>
										<Typography
											className={classNames("normal-text", {
												"yellow-text": state.interval === 1000,
												"white-text": state.interval !== 1000,
											})}>
											x1
										</Typography>
									</IconButton>
									<IconButton
										className="icon-button"
										onClick={onClickInterval(2000)}>
										<Typography
											className={classNames("normal-text", {
												"yellow-text": state.interval === 2000,
												"white-text": state.interval !== 2000,
											})}>
											x2
										</Typography>
									</IconButton>
									<IconButton
										className="icon-button"
										onClick={onClickInterval(3000)}>
										<Typography
											className={classNames("normal-text", {
												"yellow-text": state.interval === 3000,
												"white-text": state.interval !== 3000,
											})}>
											x3
										</Typography>
									</IconButton>
									<IconButton
										className="icon-button"
										onClick={onClickInterval(5000)}>
										<Typography
											className={classNames("normal-text", {
												"yellow-text": state.interval === 5000,
												"white-text": state.interval !== 5000,
											})}>
											x5
										</Typography>
									</IconButton>
									<IconButton
										className="icon-button"
										onClick={onClickInterval(10000)}>
										<Typography
											className={classNames("normal-text", {
												"yellow-text": state.interval === 10000,
												"white-text": state.interval !== 10000,
											})}>
											x10
										</Typography>
									</IconButton>
								</div>
								<div style={{ flex: 1, width: "100%" }}>
									<input
										ref={timeSliderRef}
										type="range"
										id="time-slider"
										defaultValue="0"
										className="time-slider"
										onChange={(event) => {
											if (viewerRef.current) {
												viewerRef.current.clock.currentTime =
													JulianDate.fromDate(
														new Date(
															state.gpsData[0].time +
																Number(event.target.value),
														),
													);
											}
										}}
									/>
								</div>
							</div>
						</div>
						{state.gpsData.length < 2 &&
							!state.loading &&
							account?.email === flightLog?.email && (
								<div
									style={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										height: "100%",
										position: "absolute",
										zIndex: 2,
										top: 0,
										left: "10%",
										right: "10%",
									}}>
									<Typography className="small-title-text" textAlign="justify">
										There is no movement, or GPS data was not saved properly.
										<br />- If you have not set the app to 'Always Allow' GPS
										access, ensure the app remains active at all times.
										<br />- If you were not moving, please move around while
										recording.
									</Typography>
								</div>
							)}
						{state.mapError && !state.loading && state.gpsData.length > 1 && (
							<div className="flight-map-status-message">
								<Typography className="small-title-text" textAlign="center">
									Flight data is available, but the 3D replay could not be
									initialized in this environment.
								</Typography>
							</div>
						)}
					</Grid2>
				)}
			</Grid2>
		</Container>
	);
}
